import { useAppSelector } from '@hooks/app/redux';
import useUsersProviders from '@hooks/query/provider/useUsersProviders';
import { AdressOutlined, EmailOutlined, PhoneOutlined, WebOutlined } from '@icon';
import { Provider } from '@illustrations';
import { Col, Descriptions, Divider, Hyperlink, Row, Typography } from '@ui';
import React from 'react';
import { useTheme } from 'styled-components';
import AddGuarantorModal from './components/add-guarantor-modal';
import MyWorkspaces from './components/my-workspaces';
import { StyledHeaderRow } from './Overview.styled';

const Overview: React.FunctionComponent = () => {
  const theme = useTheme();
  const providerId = useAppSelector((state) => state.users.currentProvider?.id)!;

  const { data, refetch } = useUsersProviders();

  const currentProvider = React.useMemo(() => {
    return data?.find((p) => p.provider.id === providerId) ?? null;
  }, [data, providerId]);

  if (!currentProvider) return null;

  const url = currentProvider.provider.webUrl
    ? currentProvider.provider.webUrl.startsWith('http')
      ? currentProvider.provider.webUrl
      : `https://${currentProvider.provider.webUrl}`
    : undefined;

  return (
    <>
      <StyledHeaderRow>
        <div className="img-wrapper">
          {currentProvider.provider.logoUrl ? (
            <img src={currentProvider.provider.logoUrl} alt="provider_placeholder_icon" />
          ) : (
            <Provider />
          )}
        </div>
        <Typography display="h4" variant="strong">
          {currentProvider.provider.name}
        </Typography>
      </StyledHeaderRow>
      <Divider style={{ padding: `${theme.spacings.component.M}px 0` }} />
      {currentProvider.provider.guarantor ? null : (
        <AddGuarantorModal afterSubmit={refetch} providerId={providerId} />
      )}
      <Row gutter={[16, 24]}>
        <Col xs={12} lg={6}>
          <Typography
            display="body1"
            variant="strong"
            style={{ marginBottom: theme.spacings.text.S }}
          >
            Kontaktné údaje
          </Typography>
          <Descriptions
            layout="horizontal"
            variant="on-light"
            items={[
              {
                left: <Descriptions.Icon Icon={AdressOutlined} />,
                right: `${currentProvider.provider.address.city}, ${currentProvider.provider.address.address}, ${currentProvider.provider.address.postCode}`,
              },
              {
                left: <Descriptions.Icon Icon={EmailOutlined} />,
                right: currentProvider.provider.contactEmail,
              },
              {
                left: <Descriptions.Icon Icon={PhoneOutlined} />,
                right: currentProvider.provider.contactPhone,
              },
              {
                left: <Descriptions.Icon Icon={WebOutlined} />,
                right: url ? (
                  <Hyperlink href={url} style={{ whiteSpace: 'break-spaces' }} target="_blank">
                    {new URL(url).hostname}
                  </Hyperlink>
                ) : (
                  '-'
                ),
              },
            ]}
          />
        </Col>
        <Col xs={12} lg={6}>
          <Typography
            display="body1"
            variant="strong"
            style={{ marginBottom: theme.spacings.text.S }}
          >
            Firemné údaje
          </Typography>
          <Descriptions
            layout="horizontal"
            variant="on-light"
            items={[
              {
                left: <Descriptions.Prefix>ID:</Descriptions.Prefix>,
                right: currentProvider.provider.id,
              },
              {
                left: <Descriptions.Prefix>IČO:</Descriptions.Prefix>,
                right: currentProvider.provider.companyInfo.identificationNumber,
              },
              {
                left: <Descriptions.Prefix>DIČ:</Descriptions.Prefix>,
                right: currentProvider.provider.companyInfo.taxIdentificationNumber,
              },
              {
                left: <Descriptions.Prefix>Číslo účtu:</Descriptions.Prefix>,
                right: currentProvider.provider.iban,
              },
            ]}
          />
        </Col>
      </Row>
      <Divider style={{ padding: `${theme.spacings.component.M}px 0` }} />
      <Row gutter={[16, 24]}>
        <Col xs={12}>
          <Typography
            display="body1"
            variant="strong"
            style={{ marginBottom: theme.spacings.text.S }}
          >
            Moje pridelené pracoviská
          </Typography>
          <MyWorkspaces />
        </Col>
      </Row>
    </>
  );
};

export default Overview;
